import { localeMapping } from 'helpers/constants/localeMapping';
import {
  CONTURA,
  LIANO,
  LUNA,
  URBANE,
  CARE,
  OPAL,
  WAIPORI,
  KROME,
  AIO,
  MAKU,
  TUROA,
} from 'helpers/constants/collection-names';

const BRANDS_URL = '/brands/';
const COLLECTION_URL = '/collection/';
const SECTORS_URL = '/sectors/';
const SOLUTIONS_URL = '/solutions/';

const getRelatedProductsFilterQuery = (currentURL: string, locale: string, products: any, categoryToFetch?: string, parentProductId?: string) => {
  const localeKey = localeMapping[locale];
  const { category } = getProductInfo(products, categoryToFetch);
  const productKey = parentProductId;

  const productKeySkipFilter = `NOT key:"${productKey}"`;

  const allFilters = [
    ...getCategoryFilter(category, locale),
    productKeySkipFilter,
    ...getBrandSpecificFilterQuery(),
    ...getMethvenCollectionPdpQuery(),
    ...getSolutionPagesSpecificFilterQuery(localeKey),
    ...getSectorPagesSpecificFilterQuery(),
    ...getSpecialCategoryFilterQuery(),
  ];
  return allFilters.join(' AND ');
};

const getCategoryFilter = (category: any, locale: string) => {
  const localeKey = localeMapping[locale];
  const queryPath = window.location.search;
  const isSectorPage = queryPath.indexOf(SECTORS_URL) !== -1;
  const isSolutionsPage = queryPath.indexOf(SOLUTIONS_URL) !== -1;

  if (isSolutionsPage && queryPath.indexOf('touchless') !== -1) {
    return [` NOT categoryKeys.${localeKey}:"${category}"`];
  }
  if (isSectorPage || isSolutionsPage) {
    return [];
  }
  const categoryFilter: string[] = [`categoryKeys.${localeKey}:"${category}"`];
  return categoryFilter;
};

const getProductInfo = (products: any[], categoryToFetch?: string) => {
  const queryPath = window.location.search;
  if (
    queryPath.indexOf('lead-free') !== -1 ||
    queryPath.indexOf('hobmixer') !== -1 ||
    queryPath.indexOf('touchless') !== -1
  ) {
    return {
      category: 'Tapware',
    };
  }
  return {
    category: categoryToFetch,
  };
};

const getBrandSpecificFilterQuery = () => {
  const queryPath = window.location.search;
  if (queryPath.indexOf('brands') == -1) {
    return [];
  }
  const currentProductBrand = queryPath?.split(BRANDS_URL)?.[1]?.replace('/', '');
  const filterObj = { brand: '', subBrand: '', range: '' };
  switch (currentProductBrand?.toUpperCase()) {
    case 'CAROMA':
      filterObj.brand = 'Caroma';
      break;

    case 'CAROMA-LIVEWELL':
      filterObj.brand = 'Caroma';
      filterObj.subBrand = 'LiveWell';
      break;

    case 'CAROMA-SMART-COMMAND':
      filterObj.brand = 'Caroma';
      filterObj.range = 'Smart Command';
      break;

    case 'METHVEN':
      filterObj.brand = 'Methven';
      break;

    case 'STYLUS':
      filterObj.brand = 'Stylus';
      break;

    case 'CLARK':
      filterObj.brand = 'Clark';
      break;

    case 'DORF':
      filterObj.brand = 'Dorf';
      break;
  }
  const { brand, subBrand, range } = filterObj;
  const filter = [];
  if (!!brand) {
    filter.push(`searchableAttributes.brand:"${brand}"`);
  }
  if (!!subBrand) {
    filter.push(`searchableAttributes.sub-brand:"${subBrand}"`);
  }
  if (!!range) {
    const rangesToUse = [range, 'SMART'];
    filter.push(rangesToUse.map(r => `searchableAttributes.range:"${r}"`).join(' OR '));
  }
  return filter;
};

const getMethvenCollectionPdpQuery = () => {
  const queryPath = window.location.search;
  if (queryPath.indexOf('collection') == -1) {
    return [];
  }
  const currentProductBrand = queryPath?.split(COLLECTION_URL)?.[1]?.replace('/', '');
  const brand = 'Methven',
    range = [],
    filter = [];

  switch (currentProductBrand?.toLowerCase()) {
    case WAIPORI:
      range.push('Krome');
      range.push('Maku');
      range.push('Aio');
      break;

    case KROME:
      range.push('Waipori');
      range.push('Maku');
      range.push('Aio');
      break;

    case AIO:
      range.push('Waipori');
      range.push('Krome');
      range.push('Maku');
      break;

    case MAKU:
      range.push('Waipori');
      range.push('Krome');
      range.push('Aio');
      break;

    case TUROA:
      range.push('Waipori');
      range.push('Maku');
      range.push('Aio');
      break;
  }

  if (!!brand) {
    filter.push(`searchableAttributes.brand: "${brand}"`);
  }

  if (range.length) {
    let rangeFilter = '';
    range.forEach((e, i) => {
      if (i > 0) {
        rangeFilter += ' OR ';
      }
      rangeFilter += `searchableAttributes.range: "${e}"`;
    });
    filter.push(rangeFilter);
  }
  return filter;
};

const getSolutionPagesSpecificFilterQuery = (localeKey: string) => {
  const queryPath = window.location.search;
  if (queryPath.indexOf('solutions') == -1) {
    return [];
  }
  const currentProductSolutions = queryPath?.split(SOLUTIONS_URL)?.[1]?.replace('/', '');
  const filterObj = { variantkey: '', brand: '', range: '' };
  switch (currentProductSolutions?.toUpperCase()) {
    case 'AS14281-ACCESSIBLE':
      filterObj.variantkey = 'IsAS14281Compliant';
      break;

    case 'NZ4121-ACCESSIBLE':
      filterObj.variantkey = 'IsNZ41121Compliant';
      break;

    case 'AS14281-AMBULANT':
      filterObj.variantkey = 'IsAS14281Ambulant';
      break;

    case 'INDEPENDENT-LIVING':
      filterObj.variantkey = 'IsIndependentLivingCompliant';
      break;

    case 'TOUCHLESS-BATHROOMS':
      filterObj.variantkey = 'touchless-smart';
      break;

    case 'PRE-FABRICATION-FRAMES':
      filterObj.variantkey = 'pre-fabrication-frames';
      break;

    case 'SMART-COMMAND':
      filterObj.brand = 'Caroma';
      filterObj.range = 'Smart Command';
      break;
  }
  const { variantkey, brand, range } = filterObj;

  const filter = [];
  if (!!variantkey) {
    filter.push(`variants.attributes.${variantkey}:true`);
  }
  if (!!brand) {
    filter.push(`searchableAttributes.brand:"${brand}"`);
  }
  if (!!range) {
    const rangesToUse = [range, 'SMART'];
    filter.push(rangesToUse.map(r => `searchableAttributes.range:"${r}"`).join(' OR '));
  }
  return filter;
};

const getSectorPagesSpecificFilterQuery = () => {
  const queryPath = window.location.search;
  if (queryPath.indexOf('sectors') == -1) {
    return [];
  }
  const currentProductSolutions = queryPath?.split('/sectors/')?.[1]?.replace('/', '');
  const filterObj = { variantkey: '' };

  switch (currentProductSolutions) {
    case 'healthcare':
      filterObj.variantkey = '101';
      break;

    case 'multi-residential':
      filterObj.variantkey = '102';
      break;

    case 'offices':
      filterObj.variantkey = '103';
      break;

    case 'education':
      filterObj.variantkey = '104';
      break;

    case 'retail-hospitality':
      filterObj.variantkey = '105';
      break;

    case 'hotels':
      filterObj.variantkey = '106';
      break;

    case 'entertainment-recreation-transports':
      filterObj.variantkey = '107';
      break;

    case 'government-defense':
      filterObj.variantkey = '108';
      break;
  }
  const { variantkey } = filterObj;

  const filter = [];
  if (!!variantkey) {
    filter.push(`variants.searchableAttributes.specify-sectors:${variantkey}`);
  }
  return filter;
};

const getSpecialCategoryFilterQuery = () => {
  const queryPath = window.location.search;
  const filter = [];
  if (queryPath.indexOf('lead-free') !== -1) {
    filter.push('variants.attributes.leadfree:true');
  } else if (queryPath.indexOf('hob-mixers') !== -1) {
    filter.push('variants.attributes.hobmixer:true');
  } else if (queryPath.indexOf('touchless') !== -1) {
    filter.push('variants.attributes.touchless-smart:true');
  }

  return filter;
};

/*Collection Page Filter Query*/
const getCollectionPageRPFilterQuery = (slug: string | undefined, localeKey: string) => {
  const filter = [];
  let brand = 'Caroma';
  const range = [];
  const rangeNot = [];
  const categoryKeys = [];
  switch (slug) {
    case CONTURA:
      range.push('Liano');
      range.push('Liano Care');
      range.push('Liano II');
      range.push('Liano Nexus');
      range.push('Urbane');
      range.push('Urbane II');
      break;

    case LUNA:
      range.push('Classic');
      range.push('Elegance');
      range.push('Elegance II');
      range.push('Tribute');
      break;

    case URBANE:
      range.push('Liano');
      range.push('Liano Care');
      range.push('Liano II');
      range.push('Liano Nexus');
      range.push('Contura II');
      break;

    case LIANO:
      range.push('Urbane');
      range.push('Urbane II');
      range.push('Contura II');
      break;

    case OPAL:
      categoryKeys.push('Care Sinks');
      categoryKeys.push('Care Tapware');
      categoryKeys.push('Care Showers');
      categoryKeys.push('Care Toilets');
      categoryKeys.push('Care Accessories');
      categoryKeys.push('Care Basins');
      rangeNot.push('Opal');
      rangeNot.push('Opal II Easy Height');
      rangeNot.push('Opal II');
      rangeNot.push('OpalCare');
      rangeNot.push('OPALSUPPORT');
      break;

    case CARE:
      range.push('Opal');
      range.push('Opal II');
      range.push('OpalCare');
      range.push('OPALSUPPORT');
      range.push('Opal II Easy Height');
      break;

    case WAIPORI:
      brand = 'Methven';
      range.push('Krome');
      range.push('Maku');
      range.push('Aio');
      break;

    case KROME:
      brand = 'Methven';
      range.push('Waipori');
      range.push('Maku');
      range.push('Aio');
      break;

    case AIO:
      brand = 'Methven';
      range.push('Waipori');
      range.push('Krome');
      range.push('Maku');
      break;

    case MAKU:
      brand = 'Methven';
      range.push('Waipori');
      range.push('Krome');
      range.push('Aio');
      break;

    case TUROA:
      brand = 'Methven';
      range.push('Waipori');
      range.push('Maku');
      range.push('Aio');
      break;
  }

  if (!!brand) {
    filter.push(`searchableAttributes.brand:"${brand}"`);
  }
  if (categoryKeys.length) {
    let categoryFilter = '';
    categoryKeys.forEach((e, i) => {
      if (i > 0) {
        categoryFilter += ' OR ';
      }
      categoryFilter += `categoryKeys.${localeKey}:"${e}"`;
    });
    filter.push(categoryFilter);
  }
  if (range.length) {
    let rangeFilter = '';
    range.forEach((e, i) => {
      if (i > 0) {
        rangeFilter += ' OR ';
      }
      rangeFilter += `searchableAttributes.range:"${e}"`;
    });
    filter.push(rangeFilter);
  }
  if (rangeNot.length) {
    rangeNot.forEach((e) => {
      filter.push(` NOT searchableAttributes.range:"${e}"`);
    });
  }
  return filter.join(' AND ');
};

export { getRelatedProductsFilterQuery, getCollectionPageRPFilterQuery };
